/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import { IContentBlockAdditionalContentItemViewProps, IContentBlockAdditionalContentViewProps } from './components/additional-content';
import { IContentBlockViewProps } from './mtt-content-block';

import { Swiper, SwiperSlide, SwiperRef, SwiperClass } from 'swiper/react';

import { useRef, useState } from 'react';
import chunkArray from './actions/chunckArray';
import { ICatalogOfItemsData } from './mtt-content-block.props.autogenerated';
/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */

/*masheed extension: create swiper carousel block and pagination for him*/
const createSwiperCarousel = (
    catalogOfItems: ICatalogOfItemsData[] | undefined,
    countOfItemsOnSlide: number | undefined,
    createImageBlock: ((image: Msdyn365.IImageData | undefined) => JSX.Element | undefined) | undefined
) => {
    const rows = chunkArray(catalogOfItems, countOfItemsOnSlide);

    const [activeIndex, setActiveIndex] = useState(0);

    const isIndexOnStart = activeIndex <= 0;
    const isIndexOnEnd = activeIndex >= rows.length - 1;

    const swiperRef = useRef<SwiperRef>(null);

    const slideNext = () => {
        if (activeIndex >= rows.length - 1) {
            return;
        }
        swiperRef?.current?.swiper.slideNext();
        setActiveIndex(activeIndex + 1);
    };
    const prevSlide = () => {
        if (activeIndex <= 0) {
            return;
        }
        swiperRef?.current?.swiper.slidePrev();
        setActiveIndex(activeIndex - 1);
    };
    const slideTo = (i: number) => {
        swiperRef?.current?.swiper.slideTo(i);
        setActiveIndex(i);
    };

    return (
        <>
            <Swiper
                spaceBetween={8}
                ref={swiperRef}
                onSlideChange={(swiper: SwiperClass) => {
                    setActiveIndex(swiper?.activeIndex);
                }}
            >
                {rows.map((slide, j) => (
                    <SwiperSlide className='mtt-content-block__swiper__slide-content' key={j}>
                        {slide.map((el, i) => (
                            <a href={el.link?.destinationUrl} key={i} className='mtt-content-block__swiper__slide-content__link'>
                                {createImageBlock ? createImageBlock(el.image) : null}
                                <h4 className='mtt-content-block__swiper__slide-content__heading'>{el.heading}</h4>
                            </a>
                        ))}
                    </SwiperSlide>
                ))}
            </Swiper>
            <div className='mtt-content-block__swiper__pagination'>
                <button
                    className={`mtt-content-block__swiper__pagination__arrow arrow-left ${isIndexOnStart ? 'arrow-disable' : ''}`}
                    onClick={prevSlide}
                >
                    <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    >
                        <path d='m15 18-6-6 6-6' />
                    </svg>
                </button>
                <div className='mtt-content-block__swiper__pagination__indexs'>
                    {rows.map((_, i) => (
                        <div
                            key={i}
                            className={`mtt-content-block__swiper__pagination__index ${activeIndex === i ? 'active' : ''}`}
                            onClick={() => slideTo(i)}
                        >
                            {i + 1}
                        </div>
                    ))}
                </div>
                <button
                    className={`mtt-content-block__swiper__pagination__arrow arrow-right ${isIndexOnEnd ? 'arrow-disable' : ''}`}
                    onClick={slideNext}
                >
                    <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    >
                        <path d='m9 18 6-6-6-6' />
                    </svg>
                </button>
            </div>
        </>
    );
};
/*masheed extension: create swiper carousel block and pagination for him*/

const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

const ContentBlockView: React.FC<IContentBlockViewProps> = props => {
    const {
        config,
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent,
        createImageBlock
    } = props;
    /*masheed extension: added wrapper to swiper*/
    if (config.allowAddingCatalogOfItems) {
        return (
            <div className='mtt-content-block__wrapper'>
                <div className='mtt-content-block__card'>
                    <div className='mtt-content-block__header'>{title}</div>
                    {createSwiperCarousel(config.catalogOfItems, config.countOfItemsOnSlide, createImageBlock)}
                </div>
            </div>
        );
        /*masheed extension: added wrapper to swiper*/
    } else if (imageLink) {
        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    <Node {...imageContainer}>{image}</Node>
                </Node>
                <Node {...detailsContainer}>
                    {title}
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            <Node {...imageContainer}>{image}</Node>
            <Node {...detailsContainer}>
                {title}
                {text}
                {links}
                {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
        </Module>
    );
};

export default ContentBlockView;
